/**
 * /admin/$slug/my-day — Feature 4.
 *
 * Single-day, single-staff-member focused view. Vertical hour strip
 * (07:00–19:00) with booking pills positioned at their start hour.
 *
 * Default day is computed on the server (respects tenant timezone +
 * businessHours):
 *   - If today is open AND now < close → today.
 *   - Otherwise → next open business day.
 *
 * Staff filter:
 *   - owner / admin → can switch between every teammate.
 *   - staff         → pinned to themselves; the dropdown is hidden.
 *
 * Clicking a booking opens the existing BookingMoveDialog (reuses the
 * same move flow as the main calendar) so this page intentionally
 * doesn't ship its own edit UI.
 */
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
} from '@bookaburra/ui';
import {
  AlertTriangle,
  Ban,
  ChevronLeft,
  ChevronRight,
  HandHeart,
  Lock,
  LogIn,
  MapPin,
  Plus,
} from 'lucide-react';
import { SUPPORTED_LOCALES } from '@bookaburra/i18n';
import {
  MyDayApiError,
  fetchMyDay,
  type MyDayBooking,
  type MyDayResponse,
} from '../booking-flow/my-day-api';
import { fetchMyMemberships } from '../booking-flow/tenant-api';
import { useDocumentTitle } from '../lib/use-document-title';
import { BookingMoveDialog } from '../components/BookingMoveDialog';

export const Route = createFileRoute('/admin/$slug/my-day')({
  component: MyDayPage,
  validateSearch: (
    s: Record<string, unknown>,
  ): { date?: string; staffId?: string } => ({
    date: typeof s.date === 'string' ? s.date : undefined,
    staffId: typeof s.staffId === 'string' ? s.staffId : undefined,
  }),
});

/** Hour range of the vertical strip. */
const FIRST_HOUR = 7;
const LAST_HOUR = 19;

function MyDayPage() {
  const { slug } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/admin/$slug/my-day' });
  const qc = useQueryClient();

  const [editingBookingId, setEditingBookingId] = useState<string | null>(null);

  const myDayQ = useQuery({
    queryKey: ['my-day', slug, search.date ?? null, search.staffId ?? null],
    queryFn: () =>
      fetchMyDay({ slug, date: search.date, staffId: search.staffId }),
  });

  const data = myDayQ.data;

  useDocumentTitle(
    data ? `My day · ${data.tenantName} Admin` : 'My day',
  );

  function setSearch(next: { date?: string; staffId?: string }) {
    navigate({
      to: '/admin/$slug/my-day',
      params: { slug },
      search: (prev) => ({ ...prev, ...next }),
    });
  }

  function stepDay(n: number) {
    if (!data) return;
    const next = addDaysIso(data.date, n);
    setSearch({ date: next });
  }

  function jumpToDefault() {
    // Clearing the date param lets the server pick the tenant-local
    // default again on the next fetch.
    setSearch({ date: undefined });
  }

  return (
    <main className="mx-auto max-w-3xl px-6 py-10 sm:px-12">
      <header className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-widest text-primary">
          Admin
        </p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight">My day</h1>
        <p className="text-sm text-muted-foreground">
          {data?.timezone ?? 'Australia/Sydney'}
        </p>
      </header>

      <CreateBookingCta slug={slug} />

      {myDayQ.isLoading ? <SkeletonStrip /> : null}

      {myDayQ.error instanceof MyDayApiError && myDayQ.error.status === 401 ? (
        <SignInPrompt slug={slug} />
      ) : myDayQ.error instanceof MyDayApiError && myDayQ.error.status === 403 ? (
        <NoAccessPrompt />
      ) : myDayQ.error instanceof MyDayApiError && myDayQ.error.status === 404 ? (
        <Alert variant="destructive">
          <AlertTitle>Tenant not found</AlertTitle>
          <AlertDescription>
            <code>{slug}</code> isn&rsquo;t a registered tenant.
          </AlertDescription>
        </Alert>
      ) : myDayQ.error ? (
        <Alert variant="destructive">
          <AlertTitle>Couldn&rsquo;t load your day</AlertTitle>
          <AlertDescription>{(myDayQ.error as Error).message}</AlertDescription>
        </Alert>
      ) : null}

      {data ? (
        <>
          <Controls
            data={data}
            onPrev={() => stepDay(-1)}
            onNext={() => stepDay(1)}
            onToday={jumpToDefault}
            onStaffChange={(staffId) => setSearch({ staffId })}
          />
          {data.bookings.length === 0 ? (
            <EmptyState
              date={data.date}
              timezone={data.timezone}
              onNext={() => stepDay(1)}
            />
          ) : (
            <DayStrip
              data={data}
              onBookingClick={setEditingBookingId}
            />
          )}
        </>
      ) : null}

      {editingBookingId && data ? (
        <BookingMoveDialog
          slug={slug}
          bookingId={editingBookingId}
          timezone={data.timezone}
          booking={(() => {
            const b = data.bookings.find((x) => x.id === editingBookingId);
            return b
              ? {
                  id: b.id,
                  serviceId: b.serviceId,
                  serviceName: b.serviceName,
                  rangeStart: b.rangeStart,
                  clientId: b.clientId,
                  clientName: b.clientFirstName,
                  serviceAddressFormatted: b.serviceAddressFormatted,
                }
              : null;
          })()}
          onClose={() => setEditingBookingId(null)}
          onMoved={() => {
            qc.invalidateQueries({ queryKey: ['my-day', slug] });
            setEditingBookingId(null);
          }}
          followUpHref={(clientId) =>
            `/admin/${encodeURIComponent(slug)}/create-booking?clientId=${encodeURIComponent(clientId)}`
          }
        />
      ) : null}
    </main>
  );
}

function Controls({
  data,
  onPrev,
  onNext,
  onToday,
  onStaffChange,
}: {
  data: MyDayResponse;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onStaffChange: (staffId: string) => void;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onPrev}
          aria-label="Previous day"
          leadingIcon={<ChevronLeft className="h-4 w-4" />}
        >
          Prev
        </Button>
        <Button variant="outline" size="sm" onClick={onToday}>
          Today
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onNext}
          aria-label="Next day"
          trailingIcon={<ChevronRight className="h-4 w-4" />}
        >
          Next
        </Button>
        <span className="ml-2 text-sm font-semibold text-foreground">
          {formatLongDate(data.date, data.timezone)}
        </span>
      </div>
      {data.canSwitchStaff && data.staff.length > 1 ? (
        <label className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Showing</span>
          <select
            value={data.staffId}
            onChange={(e) => onStaffChange(e.target.value)}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-sm font-medium"
          >
            {data.staff.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {s.id === data.currentUserId ? ' (you)' : ''}
              </option>
            ))}
          </select>
        </label>
      ) : null}
    </div>
  );
}

function DayStrip({
  data,
  onBookingClick,
}: {
  data: MyDayResponse;
  onBookingClick: (id: string) => void;
}) {
  const hours = useMemo(() => {
    const xs: number[] = [];
    for (let h = FIRST_HOUR; h <= LAST_HOUR; h++) xs.push(h);
    return xs;
  }, []);

  // Group bookings by start hour for placement in the vertical strip.
  // Bookings starting outside the rendered window get clipped into the
  // nearest boundary row so a 6am or 9pm appointment still surfaces.
  const byHour = useMemo(() => {
    const map = new Map<number, MyDayBooking[]>();
    for (const b of data.bookings) {
      const h = hourInZone(b.rangeStart, data.timezone);
      const clamped = Math.min(Math.max(h, FIRST_HOUR), LAST_HOUR);
      const arr = map.get(clamped) ?? [];
      arr.push(b);
      map.set(clamped, arr);
    }
    for (const arr of map.values()) arr.sort((a, b) => a.rangeStart - b.rangeStart);
    return map;
  }, [data.bookings, data.timezone]);

  return (
    <div className="overflow-hidden rounded-2xl border border-brand-sage/15 bg-card shadow-brand">
      <div className="divide-y divide-brand-sage/10">
        {hours.map((hour) => {
          const slot = byHour.get(hour);
          return (
            <div
              key={hour}
              className="grid grid-cols-[4.5rem_1fr] items-start gap-3 px-4 py-3"
            >
              <div className="pt-1 text-xs font-semibold tabular-nums text-muted-foreground">
                {formatHour(hour)}
              </div>
              <div className="space-y-2">
                {!slot || slot.length === 0 ? (
                  <div className="h-6 text-sm text-muted-foreground/40">
                    —
                  </div>
                ) : (
                  slot.map((b) => (
                    <BookingBlock
                      key={b.id}
                      booking={b}
                      timezone={data.timezone}
                      tenantDefaultLocale={data.tenantDefaultLocale}
                      onClick={() => onBookingClick(b.id)}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Mirror of DayCalendar's ClientHistoryBadge for the My day card.
 * Lives here (not in @bookaburra/ui) because the My day surface uses
 * shadcn muted-foreground tones rather than the brand-cream context
 * of the calendar pill — the colour pairings need different
 * background opacity to read against the cream-shadow card.
 */
function ClientHistoryDot({
  outcome,
}: {
  outcome: 'no_show' | 'client_cancel' | 'staff_cancel' | 'ok' | null;
}) {
  if (outcome === 'no_show') {
    return (
      <span
        title="Client no-showed their last booking — confirm reminder, consider deposit"
        className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-destructive/10 text-destructive"
      >
        <Ban className="h-3 w-3" />
      </span>
    );
  }
  if (outcome === 'client_cancel') {
    return (
      <span
        title="Client cancelled their last booking — keep an eye out"
        className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-amber-500/10 text-amber-600"
      >
        <AlertTriangle className="h-3 w-3" />
      </span>
    );
  }
  if (outcome === 'staff_cancel') {
    return (
      <span
        title="We cancelled their last booking — they're giving us another go, treat with extra care"
        className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-brand-sage/15 text-brand-sage"
      >
        <HandHeart className="h-3 w-3" />
      </span>
    );
  }
  return null;
}

function BookingBlock({
  booking,
  timezone,
  tenantDefaultLocale,
  onClick,
}: {
  booking: MyDayBooking;
  timezone: string;
  tenantDefaultLocale: string;
  onClick: () => void;
}) {
  const startLabel = new Date(booking.rangeStart).toLocaleTimeString('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone,
  });
  const endLabel = new Date(booking.rangeEnd).toLocaleTimeString('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone,
  });
  const localeLabel = localeLabelFor(booking.bookingLocale, tenantDefaultLocale);

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded-lg border border-brand-sage/20 bg-brand-cream-shadow/40 px-3 py-2.5 text-left transition-colors hover:bg-brand-cream-shadow/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-sage"
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-sm font-semibold tabular-nums text-foreground">
          {startLabel}–{endLabel}
        </span>
        <span className="text-sm font-medium text-foreground">
          {booking.serviceName}
        </span>
        {booking.clientFirstName ? (
          <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <ClientHistoryDot outcome={booking.clientLastOutcome ?? null} />
            with {booking.clientFirstName}
          </span>
        ) : null}
        {localeLabel ? (
          <Badge variant="secondary" className="text-[10px]">
            {localeLabel}
          </Badge>
        ) : null}
      </div>
      {booking.serviceAddressFormatted ? (
        <div className="mt-1 flex items-start gap-1 text-xs text-muted-foreground">
          <MapPin className="mt-0.5 h-3 w-3 shrink-0" />
          <span className="truncate">{booking.serviceAddressFormatted}</span>
        </div>
      ) : null}
    </button>
  );
}

function EmptyState({
  date,
  timezone,
  onNext,
}: {
  date: string;
  timezone: string;
  onNext: () => void;
}) {
  return (
    <Card className="p-10 text-center">
      <p className="text-sm text-muted-foreground">
        Nothing on for {formatLongDate(date, timezone)}.
      </p>
      <div className="mt-4">
        <Button
          variant="outline"
          size="sm"
          onClick={onNext}
          trailingIcon={<ChevronRight className="h-4 w-4" />}
        >
          Next day
        </Button>
      </div>
    </Card>
  );
}

function SkeletonStrip() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-12 animate-pulse rounded-lg bg-brand-cream-shadow"
        />
      ))}
    </div>
  );
}

function SignInPrompt({ slug }: { slug: string }) {
  return (
    <Card className="mx-auto max-w-md p-8 text-center">
      <Lock className="mx-auto h-8 w-8 text-muted-foreground" />
      <h2 className="mt-4 text-lg font-semibold">Sign in to continue</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        My day is only available to signed-in team members.
      </p>
      <div className="mt-6">
        <Link to="/signin" search={{ redirect: `/admin/${slug}/my-day` }}>
          <Button leadingIcon={<LogIn className="h-4 w-4" />}>Sign in</Button>
        </Link>
      </div>
    </Card>
  );
}

function NoAccessPrompt() {
  return (
    <Alert variant="destructive">
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>You don&rsquo;t have access to this tenant</AlertTitle>
      <AlertDescription>
        Your account isn&rsquo;t a member of this business. Ask the owner to
        invite you, or sign in with a different account.
      </AlertDescription>
    </Alert>
  );
}

/**
 * "+ Create booking" entry point — Feature 6. Mirrors the one on the
 * main calendar. Hidden from regular staff (their role doesn't have
 * the create-booking capability). Owner + admin see the button and the
 * tip text below it.
 */
function CreateBookingCta({ slug }: { slug: string }) {
  const meQ = useQuery({
    queryKey: ['me-memberships'],
    queryFn: fetchMyMemberships,
    staleTime: 60_000,
  });
  const role = meQ.data?.memberships.find((m) => m.tenant.slug === slug)?.role;
  if (role !== 'owner' && role !== 'manager') return null;
  return (
    <div className="mb-6 flex flex-wrap items-center gap-3">
      <Link to="/admin/$slug/create-booking" params={{ slug }}>
        <Button leadingIcon={<Plus className="h-4 w-4" />}>Create booking</Button>
      </Link>
      <p className="text-xs text-muted-foreground">
        Tip: click any existing booking to create a follow-up for that client.
      </p>
    </div>
  );
}

/* ────────────────────── helpers ────────────────────── */

function localeLabelFor(
  code: string | null,
  tenantDefaultLocale: string,
): string | null {
  if (!code) return null;
  if (code === tenantDefaultLocale) return null;
  const match = SUPPORTED_LOCALES.find((l) => l.code === code);
  return match ? match.english : code;
}

function hourInZone(epochMs: number, timezone: string): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    hour: '2-digit',
    hour12: false,
  }).formatToParts(new Date(epochMs));
  const h = parts.find((p) => p.type === 'hour')?.value ?? '0';
  return Number(h) || 0;
}

function formatHour(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

function formatLongDate(localDate: string, timezone: string): string {
  const [y, m, d] = localDate.split('-').map(Number);
  if (!y || !m || !d) return localDate;
  // Format the wall-clock date by anchoring at noon UTC of the
  // calendar day, then asking Intl to render in the tenant tz. At
  // noon UTC the calendar date is unambiguous in every IANA zone.
  const noonUtc = Date.UTC(y, m - 1, d, 12, 0, 0, 0);
  return new Date(noonUtc).toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: timezone,
  });
}

function addDaysIso(localDate: string, n: number): string {
  const [y, m, d] = localDate.split('-').map(Number);
  if (!y || !m || !d) return localDate;
  const noon = Date.UTC(y, m - 1, d + n, 12, 0, 0, 0);
  const dt = new Date(noon);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}
